// What a productConfig slot becomes when something is written to it.
//
// One definition, used by BOTH the reducer that stores the slot
// (V3_SET_PRODUCT_CONFIG) and addToConfig, which has to route on the result.
// They used to derive it separately: addToConfig dispatched and then called
// getNextUrl with the state it had captured BEFORE the dispatch, so any
// normalization the reducer applied was invisible to routing. Replacing a
// crop-configured child was the case that bit — the reducer cleared its
// `cropOptions` while completeSlot, reading the stale slot, saw them still
// present and skipped the crop prompt, so the replacement reached the cart with
// no crop selection and no way to be asked for one.

import {
  productNeedsBackgroundSelection,
  ProductLike,
} from '../../components/v3/products/productConfigPredicates';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Product = ProductLike & { id?: unknown } & Record<string, any>;

export interface Slot {
  product?: Product | null;
  // Historical slot shapes vary; everything else is read by key.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

// The choices a customer makes FOR a particular product, all of them collected
// by the slot wizard's own steps. None can survive that product being swapped
// out of the slot: a pose is an image of a crop of one product, a background is
// picked against one product's slot count, and option/crop selections are keyed
// to option sets the replacement does not share.
//
// Deliberately excludes `personalization` — a name, which the prepay branch in
// V3_SET_PRODUCT_CONFIG keeps deliberately in sync ACROSS slots, so it is not
// scoped to a product — and `usedSlotSelectionUi`, which records how the slot was
// reached rather than anything about the product.
const PRODUCT_SCOPED_SELECTIONS = [
  'background',
  'pose',
  'poses',
  'selected_crop',
  'cropOptions',
  'productOptions',
];

export const clearProductScopedSelections = (slot: Slot): Slot => {
  const kept: Slot = { ...slot };
  PRODUCT_SCOPED_SELECTIONS.forEach((key) => delete kept[key]);
  return kept;
};

// A product arriving at a slot that is not already holding that same product
// invalidates every choice made FOR whatever was there before.
//
// The absent case counts as a replacement too: ConfigScreen's mount effect pops
// `.product` before the customer picks the replacement, so by the time this runs
// the slot usually has NO product to compare against. A slot carrying a
// background but no product can only be mid-replacement — a background is never
// collected before a product — and its leftovers are stale either way.
//
// Compared by id, not identity: the cropOptions / productOptions steps re-send
// the whole slot (product included), and those must merge, not reset.
export const replacesProduct = (
  existingSlot: Slot | undefined,
  itemToAdd: Slot | undefined,
): boolean =>
  Boolean(itemToAdd?.product) &&
  itemToAdd?.product?.id !== existingSlot?.product?.id;

export const nextSlotState = (
  existingSlot: Slot | undefined,
  itemToAdd: Slot,
): Slot => {
  const merged: Slot = existingSlot
    ? {
        ...(replacesProduct(existingSlot, itemToAdd)
          ? clearProductScopedSelections(existingSlot)
          : existingSlot),
        ...itemToAdd,
      }
    : { ...itemToAdd };

  // A background can only be stored against a product that has one to composite
  // it onto. Routing skips the background page for a child that does not, but
  // routing is not the only way to reach it — a stored URL, a bookmark, or the
  // multi-step `history.go(-keys)` jump in V3_REMOVE_ITEM_FROM_CONFIG all land
  // there directly, and the background step renders the shoot's catalogue for
  // any slot. Refusing the write here is what keeps a selection the product
  // cannot use out of the completion gate and out of buildCartObject
  // (ENG-2300 / ENG-2552); background.tsx refuses to show the picker at all.
  if (merged.background && !productNeedsBackgroundSelection(merged.product)) {
    delete merged.background;
  }

  return merged;
};
